import React from 'react';

import {
  KeyboardArrowDown as BaseKeyboardArrowDownIcon,
  Edit as BaseEditIcon,
  Delete as BaseDeleteIcon
} from '@mui/icons-material';
import {
  styled,
  Typography,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Box as BaseBox,
  Accordion as BaseAccordion
} from '@mui/material';
import { ActorStageName } from 'generated/graphql';
import _ from 'lodash';
import {
  appEvents,
  formatUtil,
  PopupMenu
} from 'modules';

import * as types from '../../types';
import * as dataService from './dataService';



const InvestmentTypography = styled(Typography)({
  fontSize: 12,
  color: '#87ACB9'
});

const ContainerAccordion = styled(BaseAccordion, {
  shouldForwardProp: prop => prop !== 'defaultCursor'
})<{defaultCursor: boolean}>(({ defaultCursor }) => ({
  background: '#F0F7FA',
  borderRadius: 10,
  ...(defaultCursor && {
    '.MuiButtonBase-root': {
      '&:hover:not(.Mui-disabled)': {
        cursor: 'default'
      }
    }
  })
}));

const Accordion = styled(BaseAccordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: 'none'
}));

const Box = styled(BaseBox)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const AccordionSummaryRootBox = styled(BaseBox)({
  flex: 1
});

const TitleTypography = styled(Typography)({
  fontWeight: 700
});

const EditIcon = styled(BaseEditIcon)({
  fontSize: 14,
  color: '#87ACB9'
});

const DeleteIcon = styled(BaseDeleteIcon)({
  fontSize: 14,
  color: '#87ACB9'
});

const KeyboardArrowDownIcon = styled(BaseKeyboardArrowDownIcon)({
  color: '#87ACB9'
});

const AnimatedKeyboardArrowDownIcon = styled(KeyboardArrowDownIcon, {
  shouldForwardProp: prop => prop !== 'expanded'
})<{expanded: boolean}>(({ expanded }) => ({
  transform: `rotate(${expanded ? '180' : '0'}deg)`,
  transitionProperty: 'transform',
  transitionDuration: '150ms',
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2 ,1)',
  transitionDelay: '0ms'
}));

const NoteTitleTypography = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  color: '#1E4959'
});



export interface IProps {
  toggleExpand: boolean;
  card: types.IInvestorCard;
  stages: types.IStage[];

  onClickMoveToStage: (name: ActorStageName) => void;
}

export const FC: React.FC<IProps> = props => {
  const {
    loading,
    mutation
  } = dataService.useDeleteNoteMutation();

  const amount = React.useMemo(() =>
    formatUtil.getDollarString(props.card.potentialInvestmentAmountDollars),
  [props.card.potentialInvestmentAmountDollars]);

  const [isExpanded, setIsExpanded] = React.useState(
    window.localStorage.getItem('isExpanded') === 'true'
  );

  const [notesExpanded, setNotesExpanded] = React.useState<boolean[]>(new Array(props.card.notes.length).fill(false));



  React.useEffect(() => {
    window.localStorage.setItem('isExpanded', `${isExpanded}`);
  }, [isExpanded]);



  React.useEffect(() => {
    const sizeOfNotes = props.card.notes.length;

    if (window.localStorage.getItem('isExpanded') !== 'true') {
      setIsExpanded(props.toggleExpand);
      setNotesExpanded(new Array(sizeOfNotes).fill(props.toggleExpand));
    }
  }, [props]);



  const stageOptions = React.useMemo(() =>
    _.map(_.filter(props.stages, s => s.name !== 'favorites' && s.name !== props.card.currentStageName), (s): PopupMenu.IActionOption => ({
      type: 'action',
      label: s.displayName,
      fnAction: () => {
        props.onClickMoveToStage(s.name);
      }
    })),
  [props]);

  const options = React.useMemo((): PopupMenu.IMenuOption[] => [
    {
      type: 'nestedOptions',
      label: 'Move to column',
      options: stageOptions
    },
    {
      type: 'action',
      label: 'Add Note',
      fnAction: () => {
        appEvents.broadcast({
          type: 'modal',
          args: {
            type: 'saveNoteModal',
            investorCardId: props.card.id
          }
        });
      }
    },
    {
      type: 'action',
      label: 'Edit',
      fnAction: () => appEvents.broadcast({
        type: 'modal',
        args: {
          type: 'editInvestorCard',
          id: props.card.id
        }
      })
    }
  ], [props.card.id, stageOptions]);



  function handleChange(panel: string) {
    return (event: React.SyntheticEvent, expanded: boolean) => {
      const isMatching = panel === props.card.id;
      const hasNotes = props.card.notes.length > 0;

      const panelId = expanded ?
        (isMatching && hasNotes) || props.toggleExpand
        :
        false;

      setIsExpanded(expanded ? panelId : false);
    };
  }



  function handleClickEdit(id: string) {
    appEvents.broadcast({
      type: 'modal',
      args: {
        type: 'saveNoteModal',
        id
      }
    });
  }



  async function handleClickDelete(id: string) {
    try {
      await mutation.deleteNote(id);

    } catch (error) {
      alert(error);
    }
  }



  function handleNodeChange(nodeIndex: number) {
    return (event: React.SyntheticEvent, expanded: boolean) => {
      const newIsNoteExpanded = [...notesExpanded];
      newIsNoteExpanded[nodeIndex] = expanded;
      setNotesExpanded(newIsNoteExpanded);
    };
  }



  return (
    <>
      <ContainerAccordion
        elevation={0}
        onChange={handleChange(props.card.id)}
        defaultCursor={props.card.notes.length === 0}
        expanded={isExpanded}>
        <AccordionSummary>
          <AccordionSummaryRootBox sx={{ minHeight: 48 }}>
            <Box>
              <TitleTypography>
                {props.card.displayName}
              </TitleTypography>

              <Box>
                {props.card.notes && props.card.notes.length > 0 && (
                  <AnimatedKeyboardArrowDownIcon expanded={isExpanded} />
                )}

                <PopupMenu.FC
                  options={options}
                  iconStyle={{ color: '#87ACB9' }} />
              </Box>
            </Box>

            <InvestmentTypography>
              Potential Investment Amount: <b>{amount}</b>
            </InvestmentTypography>
          </AccordionSummaryRootBox>
        </AccordionSummary>

        <AccordionDetails>
          {_.map(props.card.notes, (note, noteIndx) => (
            <Accordion
              key={note.id}
              expanded={notesExpanded[noteIndx]}
              onChange={handleNodeChange(noteIndx)}>
              <AccordionSummary
                expandIcon={<KeyboardArrowDownIcon />}>
                <Box sx={{ width: '100%' }}>
                  <NoteTitleTypography>
                    {note.title}
                  </NoteTitleTypography>

                  <Box>
                    <IconButton
                      disabled={loading}
                      onClick={() => handleClickEdit(note.id)}>
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      disabled={loading}
                      onClick={() => handleClickDelete(note.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Typography>{note.body}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </ContainerAccordion>
    </>
  );
};
